var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '@/constants/storage-key-constants';
import { useConfigContext } from '@/hoc';
import { getItem } from '@/utils/storage';
import { withAppName } from '@/hoc/api/api-service';
import { fetchInterviewFromToken } from '@/hoc/api/interview-token-cache';
import { prewarmInterviewSession } from '@/hoc/api/session-prewarm';
/** Map a backend rejection (HTTP status + error text) to a status-screen variant. */
function variantFromResponse(httpStatus, message) {
    const m = (message || '').toLowerCase();
    if (httpStatus === 409 || m.includes('already ended') || m.includes('already completed'))
        return 'already-completed';
    if (httpStatus === 410 || m.includes('no longer valid') || m.includes('expired'))
        return 'expired';
    if (m.includes('cancel'))
        return 'cancelled';
    if (httpStatus === 403 || m.includes('not valid at this time') || m.includes('not yet'))
        return 'not-started';
    return 'invalid';
}
const isDev = process.env.NODE_ENV === 'development';
/** Placeholder metadata shown only in the dev preview fallback. */
const PREVIEW_META = {
    role: 'Frontend Engineer',
    type: 'AI Interview',
    durationMinutes: 30,
    tags: ['2-4 years', '30 min', 'Mid level'],
    description: '<p>This is preview content shown only when the backend is not connected. ' +
        'A real interview shows the actual job description here.</p>',
    jdAvailable: true,
};
function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
/** Title-case a name: first letter of every word upper, the rest lower. */
function toTitleCase(s) {
    return s
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}
/** "1-3 years" / "5+ years" / "up to 3 years" from the JD's experience range. */
function formatExperience(min, max) {
    const lo = typeof min === 'number' ? min : null;
    const hi = typeof max === 'number' ? max : null;
    // No meaningful range (both unset or an empty 0-0) → no chip.
    if ((lo == null || lo === 0) && (hi == null || hi === 0))
        return null;
    if (lo != null && hi != null)
        return lo === hi ? `${lo} years` : `${lo}-${hi} years`;
    if (lo != null)
        return `${lo}+ years`;
    if (hi != null)
        return `up to ${hi} years`;
    return null;
}
function base64UrlDecode(input) {
    const padded = input.replace(/-/g, '+').replace(/_/g, '/');
    const withPad = padded + '='.repeat((4 - (padded.length % 4)) % 4);
    // atob is fine for the ASCII JSON payload used by the interview token.
    return atob(withPad);
}
/**
 * Decode the candidate payload from a Django-signed interview token of the form
 * `<base64url-json>:<sig>:<sig>`. Returns null if the payload can't be parsed.
 */
export function decodeInterviewToken(token) {
    try {
        const payloadSegment = token.split(':')[0];
        if (!payloadSegment)
            return null;
        const json = base64UrlDecode(payloadSegment);
        const data = JSON.parse(json);
        return {
            slug: typeof (data === null || data === void 0 ? void 0 : data.slug) === 'string' ? data.slug : undefined,
            email: typeof (data === null || data === void 0 ? void 0 : data.email) === 'string' ? data.email : undefined,
            name: typeof (data === null || data === void 0 ? void 0 : data.name) === 'string' ? data.name : undefined,
        };
    }
    catch (_a) {
        return null;
    }
}
export function readUrl() {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/');
    const slug = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2] || null;
    const token = url.searchParams.get('token');
    return { slug, token };
}
/**
 * VA-27633: the interview token, from the URL or — once the interview has
 * started — from storage.
 *
 * The token is deliberately stripped from the address bar right after the
 * session resolves, so it cannot leak through history or a screen share. The
 * side effect was that ANY reload lost it: bootstrap found no token, could not
 * authenticate, and the candidate landed on an error instead of their
 * interview. That silently broke the "Reload the page" recovery and the
 * one-click Resume screen — the two things a stuck candidate is told to use.
 *
 * The token is already persisted with the session details before the strip, so
 * this only has to read it back. Slug-scoped, so a stale token from a previous
 * interview can never be picked up.
 */
export function resolveInterviewToken(slug, urlToken) {
    return __awaiter(this, void 0, void 0, function* () {
        if (urlToken)
            return urlToken;
        try {
            const stored = (yield getItem(STORAGE_KEYS.INTERVIEW_RESPONSE));
            if (!(stored === null || stored === void 0 ? void 0 : stored.token))
                return null;
            if (slug && stored.slug && stored.slug !== slug)
                return null;
            console.info('[bootstrap] token recovered from storage (stripped from URL)');
            return stored.token;
        }
        catch (_a) {
            return null;
        }
    });
}
/**
 * Fetch the candidate-safe JD from the dedicated token-scoped endpoint
 * (`/api/get-jd-from-token/`). Returns null on any failure so the caller can
 * degrade gracefully.
 */
function fetchCandidateJd(apiBaseUrl, token) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield fetch(withAppName(`${apiBaseUrl}/api/get-jd-from-token/?token=${encodeURIComponent(token)}`), { cache: 'no-store' });
            if (!res.ok)
                return null;
            return (yield res.json());
        }
        catch (_a) {
            return null;
        }
    });
}
/**
 * Build the candidate-facing interview metadata from the dedicated candidate-safe
 * JD payload (preferred), falling back to the interview token response for
 * role/duration when the JD fetch didn't return them.
 */
function buildMeta(jd, interview) {
    var _a;
    const src = jd || {};
    const role = (typeof src.title === 'string' && src.title.trim() && src.title) ||
        (interview && typeof interview.title === 'string' ? interview.title : undefined);
    const durationRaw = (_a = src.interview_duration) !== null && _a !== void 0 ? _a : interview === null || interview === void 0 ? void 0 : interview.interview_duration;
    const duration = typeof durationRaw === 'number'
        ? durationRaw
        : typeof durationRaw === 'string' && durationRaw.trim()
            ? Number(durationRaw)
            : undefined;
    const description = typeof src.description === 'string' && src.description.trim() ? src.description : undefined;
    const durationMinutes = Number.isFinite(duration) ? duration : undefined;
    // Card chips: experience range, interview duration, interview level.
    const tags = [];
    const exp = formatExperience(src.min_experience, src.max_experience);
    if (exp)
        tags.push(exp);
    if (durationMinutes)
        tags.push(`${durationMinutes} min`);
    if (typeof src.interview_level === 'string' && src.interview_level.trim()) {
        tags.push(`${capitalize(src.interview_level)} level`);
    }
    return {
        role,
        type: 'AI Interview',
        durationMinutes,
        tags,
        description,
        jdAvailable: Boolean(description),
    };
}
export function useInterviewBootstrap() {
    const { config: { apiBaseUrl }, } = useConfigContext();
    const [state, setState] = useState({
        status: 'loading',
        candidate: null,
        meta: {},
        error: null,
        variant: null,
        isPreview: false,
    });
    const resolve = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const { slug, token: urlToken } = readUrl();
        // VA-27633: fall back to the stored token — the URL copy is stripped
        // once the interview starts, so a reload has none.
        const token = yield resolveInterviewToken(slug, urlToken);
        // Even on a terminal/error state, decode the candidate from the token so the
        // header avatar/email show the real person (not the default initial).
        const decodedCandidate = (() => {
            if (!token)
                return null;
            const d = decodeInterviewToken(token);
            if (!(d === null || d === void 0 ? void 0 : d.name) && !(d === null || d === void 0 ? void 0 : d.email))
                return null;
            return {
                name: toTitleCase(d.name || 'Candidate'),
                email: d.email || '',
                slug: d.slug || slug || '',
            };
        })();
        const setTerminal = (variant, message) => setState({
            status: 'terminal',
            candidate: decodedCandidate,
            meta: {},
            error: message,
            variant,
            isPreview: false,
        });
        // Dev-only escape hatch: when the backend is UNREACHABLE (not when it
        // returns a definitive rejection), decode the token locally so screens
        // still render for design work. A real rejection always shows its page.
        const previewOrTerminal = (reason, fallback) => {
            if (!isDev || !token)
                return setTerminal(fallback, reason);
            const decoded = decodeInterviewToken(token);
            if (!(decoded === null || decoded === void 0 ? void 0 : decoded.email) && !(decoded === null || decoded === void 0 ? void 0 : decoded.name))
                return setTerminal(fallback, reason);
            // eslint-disable-next-line no-console
            console.warn('[ui-preview] backend unreachable — rendering with decoded token data', { reason });
            setState({
                status: 'preview',
                candidate: {
                    name: toTitleCase(decoded.name || 'Candidate'),
                    email: decoded.email || '',
                    slug: decoded.slug || slug || '',
                },
                meta: PREVIEW_META,
                error: null,
                variant: null,
                isPreview: true,
            });
        };
        // DEV design mode: `?uipreview=1` skips the backend entirely and renders the
        // whole journey on decoded-token data, so the Setup/Guidelines/Meeting UI can
        // be designed without joining a real interview.
        if (isDev &&
            token &&
            typeof window !== 'undefined' &&
            new URLSearchParams(window.location.search).has('uipreview')) {
            previewOrTerminal('UI preview mode (?uipreview=1)', 'invalid');
            return;
        }
        // Reuse a cached, same-interview response so we don't re-validate on every
        // mount / screen change (the cache is only written after a prior success).
        const cached = (yield getItem(STORAGE_KEYS.INTERVIEW_RESPONSE));
        if (cached && cached.slug === slug && cached.email) {
            const jdToken = token || cached.token;
            const jd = jdToken ? yield fetchCandidateJd(apiBaseUrl, jdToken) : null;
            setState({
                status: 'valid',
                candidate: { name: toTitleCase(cached.name), email: cached.email, slug: cached.slug },
                meta: buildMeta(jd, null),
                error: null,
                variant: null,
                isPreview: false,
            });
            return;
        }
        if (!token) {
            previewOrTerminal('Missing token in URL — please use the interview link from your email', 'invalid');
            return;
        }
        // Reuse the meeting widget's already-validated token result (deduped in
        // interview-token-cache) instead of re-hitting the backend on every mount.
        let result;
        try {
            result = yield fetchInterviewFromToken(apiBaseUrl, token);
        }
        catch (_e) {
            // Network / server unreachable — dev preview, else generic invalid.
            previewOrTerminal('Unable to reach the interview server. Please try again.', 'invalid');
            return;
        }
        // Backend reached and the interview is NOT joinable → show the matching
        // status page. We must verify before ever proceeding to setup.
        if (!result.ok) {
            const detail = result.errorText || `Error ${result.status}`;
            setTerminal(variantFromResponse(result.status, String(detail)), String(detail));
            return;
        }
        const data = result.data || {};
        if (data === null || data === void 0 ? void 0 : data.ended_at) {
            setTerminal('already-completed', 'Interview already completed');
            return;
        }
        // VA-26877: verified joinable, so start building the realtime session NOW.
        // This is the earliest point it is safe to: the interview is known to be
        // valid, and the candidate still has the device check and the guidelines
        // ahead of them (22s in one measured run). Not awaited — see the helper.
        void prewarmInterviewSession(apiBaseUrl, token);
        // Verified joinable → proceed to setup.
        const jd = yield fetchCandidateJd(apiBaseUrl, token);
        setState({
            status: 'valid',
            candidate: {
                name: toTitleCase((_a = data === null || data === void 0 ? void 0 : data.interviewee_name) !== null && _a !== void 0 ? _a : 'Candidate'),
                email: (_b = data === null || data === void 0 ? void 0 : data.interviewee_email) !== null && _b !== void 0 ? _b : '',
                slug: (_d = (_c = data === null || data === void 0 ? void 0 : data.slug) !== null && _c !== void 0 ? _c : slug) !== null && _d !== void 0 ? _d : '',
            },
            meta: buildMeta(jd, data),
            error: null,
            variant: null,
            isPreview: false,
        });
    }), [apiBaseUrl]);
    useEffect(() => {
        void resolve();
    }, [resolve]);
    return state;
}
